import * as React from 'react'

type Theme = 'light' | 'dark' | 'system'
type ResolvedTheme = Exclude<Theme, 'system'>

interface ThemeProviderProps {
  children: React.ReactNode
  defaultTheme?: Theme
  enableSystem?: boolean
  disableTransitionOnChange?: boolean
  storageKey?: string
}

interface ThemeContextValue {
  theme: Theme
  resolvedTheme: ResolvedTheme
  setTheme: (theme: Theme) => void
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null)

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') {
    return 'dark'
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function applyTheme(resolvedTheme: ResolvedTheme, disableTransitionOnChange: boolean) {
  const root = document.documentElement
  const disableTransitionsStyle = disableTransitionOnChange
    ? document.createElement('style')
    : null

  if (disableTransitionsStyle) {
    disableTransitionsStyle.appendChild(
      document.createTextNode('* { transition: none !important; }')
    )
    document.head.appendChild(disableTransitionsStyle)
  }

  root.classList.remove('light', 'dark')
  root.classList.add(resolvedTheme)
  root.style.colorScheme = resolvedTheme

  if (disableTransitionsStyle) {
    void root.offsetHeight
    window.setTimeout(() => {
      disableTransitionsStyle.remove()
    }, 0)
  }
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  enableSystem = true,
  disableTransitionOnChange = false,
  storageKey = 'theme',
}: ThemeProviderProps) {
  const [theme, setThemeState] = React.useState<Theme>(defaultTheme)
  const [resolvedTheme, setResolvedTheme] = React.useState<ResolvedTheme>('dark')

  React.useEffect(() => {
    const savedTheme = typeof window === 'undefined'
      ? null
      : window.localStorage.getItem(storageKey)

    const nextTheme = savedTheme === 'light' || savedTheme === 'dark' || savedTheme === 'system'
      ? savedTheme
      : defaultTheme

    setThemeState(nextTheme)
  }, [defaultTheme, storageKey])

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const syncTheme = () => {
      const systemTheme = getSystemTheme()
      const nextResolvedTheme = theme === 'system' && enableSystem ? systemTheme : theme === 'system' ? 'dark' : theme
      setResolvedTheme(nextResolvedTheme)
      applyTheme(nextResolvedTheme, disableTransitionOnChange)
    }

    syncTheme()
    mediaQuery.addEventListener('change', syncTheme)
    return () => mediaQuery.removeEventListener('change', syncTheme)
  }, [theme, enableSystem, disableTransitionOnChange])

  const setTheme = React.useCallback((nextTheme: Theme) => {
    setThemeState(nextTheme)
    window.localStorage.setItem(storageKey, nextTheme)
  }, [storageKey])

  const value = React.useMemo<ThemeContextValue>(() => ({
    theme,
    resolvedTheme,
    setTheme,
  }), [theme, resolvedTheme, setTheme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = React.useContext(ThemeContext)

  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }

  return context
}
